// src/components/CustomerHistoryView.tsx
import React, { useState } from 'react';
import { ArrowLeft, Save, Edit3, Users, Tag, Repeat } from 'lucide-react';
import { formatPrice } from '../utils/formatters';

interface CustomerHistoryViewProps {
    selectedCustomerForHistory: any;
    jobs: any[];
    onRepeatJob: (job: any) => void;
    
    // Functies
    setActiveTab: (tab: string) => void;
    getRacketName: (job: any) => string;
    
    // Bewerk logica
    isEditingCustomer: boolean;
    startEditingCustomer: () => void;
    saveEditedCustomer: () => void;
    editingCustomerData: any;
    setEditingCustomerData: (data: any) => void;
}

export default function CustomerHistoryView({
    selectedCustomerForHistory,
    jobs,
    onRepeatJob,
    setActiveTab,
    getRacketName,
    isEditingCustomer,
    startEditingCustomer,
    saveEditedCustomer,
    editingCustomerData,
    setEditingCustomerData
}: CustomerHistoryViewProps) {

    // --- LOKALE STATE ---
    const [customerHistorySortConfig, setCustomerHistorySortConfig] = useState({ key: 'date', direction: 'descending' });

    if (!selectedCustomerForHistory) return <div>Geen klant geselecteerd</div>;
    
    // --- LOGICA ---
    let customerJobs = jobs.filter(j => j.customerId === selectedCustomerForHistory.id);
    
    customerJobs.sort((a, b) => {
        let valA, valB;
        if (customerHistorySortConfig.key === 'date') {
            valA = new Date(a.date).getTime() || 0; valB = new Date(b.date).getTime() || 0;
        } else if (customerHistorySortConfig.key === 'price') {
            valA = parseFloat(a.price) || 0; valB = parseFloat(b.price) || 0;
        } else if (customerHistorySortConfig.key === 'racket') {
            valA = getRacketName(a).toLowerCase(); valB = getRacketName(b).toLowerCase();
        } else if (customerHistorySortConfig.key === 'string') {
            valA = (a.string || '').toLowerCase(); valB = (b.string || '').toLowerCase();
        } else if (customerHistorySortConfig.key === 'stringer') {
            valA = (a.stringer || '').toLowerCase(); valB = (b.stringer || '').toLowerCase();
        } else {
            valA = (a[customerHistorySortConfig.key] || '').toString().toLowerCase();
            valB = (b[customerHistorySortConfig.key] || '').toString().toLowerCase();
        }
        if (valA < valB) return customerHistorySortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return customerHistorySortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });

// --- WEERGAVE ---
    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* KLANT HEADER (De code die je al had, maar dan compleet) */}
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button onClick={() => setActiveTab('customers')} className="p-2 mr-4 bg-slate-100 dark:bg-slate-800 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition"><ArrowLeft className="w-6 h-6 text-slate-600 dark:text-slate-300" /></button>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Klant Dossier</h2>
                </div>
                <button 
                  onClick={() => isEditingCustomer ? saveEditedCustomer() : startEditingCustomer()} 
                  className={`flex items-center px-4 py-2 rounded-xl text-white font-medium transition ${isEditingCustomer ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                    {isEditingCustomer ? <Save className="w-4 h-4 mr-2"/> : <Edit3 className="w-4 h-4 mr-2"/>}
                    {isEditingCustomer ? "Opslaan" : "Bewerken"}
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row gap-6">
                <div className="flex items-center"><div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 flex items-center justify-center font-bold text-3xl">{selectedCustomerForHistory.name.charAt(0)}</div></div>
                
                <div className="flex-1 space-y-2">
                    {isEditingCustomer ? (
                        <div className="grid grid-cols-1 gap-3">
                            <div><label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1">Naam</label><input type="text" className="text-xl font-bold p-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded w-full" value={editingCustomerData.name} onChange={e => setEditingCustomerData({...editingCustomerData, name: e.target.value})} /></div>
                            <div className="flex gap-2">
                              <div className="w-1/2"><label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1">Club</label><input type="text" className="p-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded w-full" value={editingCustomerData.club} onChange={e => setEditingCustomerData({...editingCustomerData, club: e.target.value})} /></div>
                              <div className="w-1/2"><label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1">Telefoon</label><input type="text" className="p-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded w-full" value={editingCustomerData.phone} onChange={e => setEditingCustomerData({...editingCustomerData, phone: e.target.value})} /></div>
                            </div>
                            <div className="flex gap-2">
                              <div className="w-1/3"><label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1">Merk Racket</label><input type="text" className="p-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded w-full" value={editingCustomerData.defaultRacketBrand} onChange={e => setEditingCustomerData({...editingCustomerData, defaultRacketBrand: e.target.value})} /></div>
                              <div className="w-1/3"><label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1">Model</label><input type="text" className="p-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded w-full" value={editingCustomerData.defaultRacketModel} onChange={e => setEditingCustomerData({...editingCustomerData, defaultRacketModel: e.target.value})} /></div>
                              <div className="w-1/3"><label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1">Snaar</label><input type="text" className="p-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded w-full" value={editingCustomerData.defaultString} onChange={e => setEditingCustomerData({...editingCustomerData, defaultString: e.target.value})} /></div>
                            </div>
                        </div>
                    ) : (
                        <>
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{selectedCustomerForHistory.name}</h3>
                          <p className="text-slate-500 dark:text-slate-400 flex items-center"><Users className="w-4 h-4 mr-2" /> {selectedCustomerForHistory.club || 'Geen club'} • {selectedCustomerForHistory.phone}</p>
                          <p className="text-slate-500 dark:text-slate-400 flex items-center"><Tag className="w-4 h-4 mr-2" /> {selectedCustomerForHistory.defaultRacketBrand} {selectedCustomerForHistory.defaultRacketModel} • {selectedCustomerForHistory.defaultString}</p>
                        </>
                    )}
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl min-w-[200px]">
                    <p className="text-xs text-slate-400 uppercase font-bold mb-1">Totaal Uitgegeven</p>
                    <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">€ {formatPrice(customerJobs.reduce((acc, job) => acc + (job.price || 0), 0))}</p>
                    <p className="text-xs text-slate-400 mt-1">{customerJobs.length} opdrachten</p>
                </div>
            </div>

            {/* HIER BEGINT DE TABEL MET OPDRACHTEN DIE WE MISTEN! */}
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mt-8 mb-4">Opdrachten Historie</h3>
            
            {customerJobs.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 text-center text-slate-500 dark:text-slate-400">
                    Nog geen opdrachten voor deze klant.
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 text-xs uppercase font-bold text-slate-500 dark:text-slate-400">
                                    <th className="p-4 cursor-pointer hover:text-indigo-600 transition" onClick={() => setCustomerHistorySortConfig({ key: 'date', direction: customerHistorySortConfig.key === 'date' && customerHistorySortConfig.direction === 'ascending' ? 'descending' : 'ascending' })}>Datum</th>
                                    <th className="p-4 cursor-pointer hover:text-indigo-600 transition" onClick={() => setCustomerHistorySortConfig({ key: 'racket', direction: customerHistorySortConfig.key === 'racket' && customerHistorySortConfig.direction === 'ascending' ? 'descending' : 'ascending' })}>Racket</th>
                                    <th className="p-4 cursor-pointer hover:text-indigo-600 transition" onClick={() => setCustomerHistorySortConfig({ key: 'string', direction: customerHistorySortConfig.key === 'string' && customerHistorySortConfig.direction === 'ascending' ? 'descending' : 'ascending' })}>Snaar</th>
                                    <th className="p-4">Kilo's</th>
                                    <th className="p-4">Bespanner</th>
                                    <th className="p-4 cursor-pointer hover:text-indigo-600 transition" onClick={() => setCustomerHistorySortConfig({ key: 'price', direction: customerHistorySortConfig.key === 'price' && customerHistorySortConfig.direction === 'ascending' ? 'descending' : 'ascending' })}>Prijs</th>
                                    <th className="p-4 text-right">Actie</th> {/* Toegevoegd voor de herhaalknop */}
                                </tr>
                            </thead>
                            <tbody>
                                {customerJobs.map(job => (
                                    <tr key={job.id} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
                                        <td className="p-4 text-sm font-medium text-slate-800 dark:text-white whitespace-nowrap">
                                            {job.date ? new Date(job.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Onbekend'}
                                        </td>
                                        <td className="p-4 text-sm text-slate-600 dark:text-slate-300 font-medium">
                                            {getRacketName(job) || 'Onbekend'}
                                        </td>
                                        <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                                            {job.isHybrid ? `${job.string || '?'} / ${job.crossString || '?'}` : (job.string || 'Onbekend')}
                                        </td>
                                        <td className="p-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                            {job.tensionMain} / {job.tensionCross} kg
                                        </td>
                                        <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                                            {job.stringer || '-'}
                                        </td>
                                        <td className="p-4 text-sm font-bold text-indigo-600 dark:text-indigo-400">
                                            € {formatPrice(job.price || 0)}
                                        </td>
                                        <td className="p-4 text-sm text-slate-600 dark:text-slate-300 text-right">
                                            <button 
                                                onClick={() => onRepeatJob(job)}
                                                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition"
                                            >
                                                <Repeat className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}